import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Badge, Modal } from '../../../components/ui';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Lock,
  RefreshCw,
  Loader2,
  Banknote,
  Eye,
  Search,
} from 'lucide-react';
import { useAuth } from '../../../app/auth';
import { fetchAPI } from '../../../services/apiClient';
import { formatTimeCR } from '../../../utils/date';
import {
  getCierresCaja,
  getDetalleCierreVendedor,
  anularCierreVendedor,
  type CierreCajaHistorico,
  type DetalleCierreVendedor,
} from '../services/cajaLoteriaService';
import {
  getSesionesHoy,
  editarSaldoInicial,
  type SesionHoy,
} from '../services/cajaVendedorService';

// ========================
// Types
// ========================

interface VendedorItem {
  id: string;
  username: string;
  nombre: string;
  role: string;
  activo: boolean;
}

// ========================
// Helpers
// ========================

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '₡0';
  return '₡' + num.toLocaleString('es-CR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function estadoBadge(estado: string) {
  switch (estado) {
    case 'CUADRA':
      return <Badge className="bg-green-100 text-green-800 border-green-300"><CheckCircle className="w-3 h-3 mr-1" /> Cuadra</Badge>;
    case 'SOBRA':
      return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300"><AlertTriangle className="w-3 h-3 mr-1" /> Sobra</Badge>;
    case 'FALTA':
      return <Badge className="bg-red-100 text-red-800 border-red-300"><XCircle className="w-3 h-3 mr-1" /> Falta</Badge>;
    default:
      return <Badge>{estado}</Badge>;
  }
}

// ========================
// Component — LOCAL Supervision (READ-ONLY)
// ========================

export const CierreLoteriasPage: React.FC = () => {
  const { user } = useAuth();

  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Vendedores
  const [vendedores, setVendedores] = useState<VendedorItem[]>([]);
  const [filtroVendedorId, setFiltroVendedorId] = useState('');

  // Historicos (lista principal)
  const [historicos, setHistoricos] = useState<CierreCajaHistorico[]>([]);

  // Detalle modal (READ-ONLY)
  const [detalleModal, setDetalleModal] = useState<DetalleCierreVendedor | null>(null);
  const [showDetalleModal, setShowDetalleModal] = useState(false);

  // Anular modal
  const [showAnularModal, setShowAnularModal] = useState(false);
  const [anularCierreId, setAnularCierreId] = useState<string | null>(null);
  const [motivoAnulacion, setMotivoAnulacion] = useState('');
  const [anulando, setAnulando] = useState(false);

  // Cajas de hoy (LOCAL)
  const [sesionesHoy, setSesionesHoy] = useState<SesionHoy[]>([]);
  const [editandoSaldo, setEditandoSaldo] = useState<Record<string, string>>({});
  const [guardandoSaldo, setGuardandoSaldo] = useState<Record<string, boolean>>({});

  // ========================
  // Data loading
  // ========================

  const cargarVendedores = useCallback(async () => {
    try {
      const data = await fetchAPI<VendedorItem[]>('/usuarios/local/vendedores');
      setVendedores(data);
    } catch (e: any) {
      setError(e.message || 'Error al cargar vendedores');
    }
  }, []);
    const imprimirCierre = (cierreId: string) => {
      const url = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/caja-vendedor/cierre/${cierreId}/imprimir`;
      window.open(url, '_blank', 'width=900,height=800');
    };

  const cargarHistoricos = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCierresCaja(filtroVendedorId || undefined);
      setHistoricos(data);
    } catch (e: any) {
      setError(e.message || 'Error al cargar cierres');
    } finally {
      setLoading(false);
    }
  }, [filtroVendedorId]);

  useEffect(() => { cargarVendedores(); }, [cargarVendedores]);
  useEffect(() => { cargarHistoricos(); }, [cargarHistoricos]);

  const cargarSesionesHoy = useCallback(async () => {
    if (user?.role !== 'LOCAL' && user?.role !== 'BANCA') return;
    try {
      const res = await getSesionesHoy();
      setSesionesHoy(res.sesiones);
      // Inicializar valores de edición con los actuales
      const vals: Record<string, string> = {};
      for (const s of res.sesiones) {
        vals[s.vendedor_id] = s.saldo_inicial;
      }
      setEditandoSaldo(vals);
    } catch {
      // silencioso — sección opcional
    }
  }, [user?.role]);

  useEffect(() => { cargarSesionesHoy(); }, [cargarSesionesHoy]);

  const handleGuardarSaldo = async (vendedorId: string) => {
    const valor = parseFloat(editandoSaldo[vendedorId] || '0');
    if (isNaN(valor) || valor < 0) return;
    setGuardandoSaldo(prev => ({ ...prev, [vendedorId]: true }));
    try {
      await editarSaldoInicial(vendedorId, valor);
      await cargarSesionesHoy();
      setSuccessMsg('Saldo inicial actualizado.');
    } catch (e: any) {
      setError(e.message || 'Error al actualizar saldo inicial');
    } finally {
      setGuardandoSaldo(prev => ({ ...prev, [vendedorId]: false }));
    }
  };

  // ========================
  // Ver detalle (READ-ONLY)
  // ========================

  const verDetalle = async (cierreId: string) => {
    try {
      const det = await getDetalleCierreVendedor(cierreId);
      setDetalleModal(det);
      setShowDetalleModal(true);
    } catch (e: any) {
      setError(e.message || 'Error al cargar detalle');
    }
  };

  // ========================
  // Anular
  // ========================

  const handleAnular = async () => {
    if (!anularCierreId || motivoAnulacion.length < 5) return;
    setAnulando(true);
    try {
      await anularCierreVendedor(anularCierreId, motivoAnulacion);
      setSuccessMsg('Cierre anulado exitosamente.');
      setShowAnularModal(false);
      setMotivoAnulacion('');
      setAnularCierreId(null);
      cargarHistoricos();
    } catch (e: any) {
      setError(e.message || 'Error al anular cierre');
    } finally {
      setAnulando(false);
    }
  };

  // ========================
  // Helpers
  // ========================

  const getVendedorNombre = (vendedorId: string) => {
    const v = vendedores.find(ve => ve.id === vendedorId);
    return v ? `${v.nombre} (${v.username})` : vendedorId;
  };

  // ========================
  // Render
  // ========================

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Banknote className="w-7 h-7 text-indigo-600" />
            Cierres de Caja
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Supervisión de cajas cerradas por los vendedores
          </p>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-3 text-red-800 text-sm">
          <XCircle className="w-4 h-4 inline mr-1" /> {error}
          <button onClick={() => setError(null)} className="ml-2 underline text-red-600">Cerrar</button>
        </div>
      )}
      {successMsg && (
        <div className="bg-green-50 border border-green-300 rounded-lg p-3 text-green-800 text-sm">
          <CheckCircle className="w-4 h-4 inline mr-1" /> {successMsg}
          <button onClick={() => setSuccessMsg(null)} className="ml-2 underline text-green-600">Cerrar</button>
        </div>
      )}

      {/* Cajas de hoy — solo LOCAL/BANCA */}
      {(user?.role === 'LOCAL' || user?.role === 'BANCA') && sesionesHoy.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
              <Banknote className="w-5 h-5 text-indigo-500" />
              Cajas Abiertas Hoy
            </h2>
            <Button size="sm" variant="outline" onClick={cargarSesionesHoy}>
              <RefreshCw className="w-3 h-3 mr-1" /> Actualizar
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-600 text-left">
                  <th className="px-3 py-2 font-medium">Vendedor</th>
                  <th className="px-3 py-2 font-medium">Estado</th>
                  <th className="px-3 py-2 font-medium">Apertura</th>
                  <th className="px-3 py-2 font-medium text-right">Saldo Inicial</th>
                  {user?.role === 'LOCAL' && <th className="px-3 py-2 font-medium text-center">Editar</th>}
                </tr>
              </thead>
              <tbody>
                {sesionesHoy.map(s => (
                  <tr key={s.vendedor_id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium">{s.vendedor_nombre}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        s.estado === 'ABIERTA' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {s.estado}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-500 text-xs">
                      {s.abierta_at ? formatTimeCR(s.abierta_at) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {user?.role === 'LOCAL' ? (
                        <input
                          type="number"
                          min="0"
                          step="1"
                          className="w-28 border border-gray-300 rounded px-2 py-1 text-right text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          value={editandoSaldo[s.vendedor_id] ?? s.saldo_inicial}
                          onChange={e => setEditandoSaldo(prev => ({ ...prev, [s.vendedor_id]: e.target.value }))}
                        />
                      ) : (
                        formatCurrency(s.saldo_inicial)
                      )}
                    </td>
                    {user?.role === 'LOCAL' && (
                      <td className="px-3 py-2 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleGuardarSaldo(s.vendedor_id)}
                          disabled={guardandoSaldo[s.vendedor_id]}
                        >
                          {guardandoSaldo[s.vendedor_id] ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Guardar'}
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Filtros */}
      <Card className="p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Search className="w-4 h-4 inline mr-1" />
              Filtrar por Vendedor
            </label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={filtroVendedorId}
              onChange={e => setFiltroVendedorId(e.target.value)}
            >
              <option value="">— Todos los vendedores —</option>
              {vendedores.map(v => (
                <option key={v.id} value={v.id}>{v.nombre} ({v.username})</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <Button size="sm" variant="outline" onClick={cargarHistoricos} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Actualizar
            </Button>
          </div>
        </div>
      </Card>

      {/* Lista de cierres */}
      <Card className="p-4">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">
          Cierres Registrados
          <span className="ml-2 text-sm font-normal text-gray-500">({historicos.length} resultados)</span>
          {user?.role === 'LOCAL' && (
            <span className="ml-2 text-xs font-normal text-gray-400">(últimos 10 por vendedor)</span>
          )}
        </h2>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando cierres…
          </div>
        ) : historicos.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <Banknote className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No hay cierres de caja registrados{filtroVendedorId ? ' para este vendedor' : ''}.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-600 text-left">
                  <th className="px-3 py-2 font-medium">Fecha</th>
                  <th className="px-3 py-2 font-medium">Vendedor</th>
                  <th className="px-3 py-2 font-medium text-right">Teórico</th>
                  <th className="px-3 py-2 font-medium text-right">Contado</th>
                  <th className="px-3 py-2 font-medium text-right">Diferencia</th>
                  <th className="px-3 py-2 font-medium text-center">Estado</th>
                  <th className="px-3 py-2 font-medium text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {historicos.map(h => (
                  <tr key={h.id} className={`border-t hover:bg-gray-50 ${h.anulado ? 'opacity-50' : ''}`}>
                    <td className="px-3 py-2">{h.fecha}</td>
                    <td className="px-3 py-2">{getVendedorNombre(h.vendedor_id)}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(h.saldo_teorico)}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(h.efectivo_contado)}</td>
                    <td className={`px-3 py-2 text-right font-medium ${
                      parseFloat(h.diferencia) > 0 ? 'text-yellow-700' : parseFloat(h.diferencia) < 0 ? 'text-red-700' : 'text-green-700'
                    }`}>
                      {formatCurrency(h.diferencia)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {h.anulado ? (
                        <Badge className="bg-gray-100 text-gray-600 border-gray-300">Anulado</Badge>
                      ) : (
                        estadoBadge(h.estado_diferencia)
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          className="text-indigo-600 hover:text-indigo-800" title="Ver detalle"
                          onClick={() => verDetalle(h.id)}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                          <button
                            className="text-blue-600 hover:text-blue-800"
                            title="Imprimir cierre"
                            onClick={() => imprimirCierre(h.id)}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                            </svg>
                          </button>
                        {!h.anulado && user?.role === 'BANCA' && (
                          <button
                            className="text-red-500 hover:text-red-700 text-xs underline"
                            onClick={() => {
                              setAnularCierreId(h.id);
                              setShowAnularModal(true);
                            }}
                          >
                            Anular
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal de detalle (READ-ONLY) */}
      <Modal isOpen={showDetalleModal} onClose={() => setShowDetalleModal(false)} title="Detalle del Cierre de Caja">
        {detalleModal && (
          <>
            <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-gray-500">Vendedor:</span> <strong>{getVendedorNombre(detalleModal.vendedor_id || '')}</strong></div>
              <div><span className="text-gray-500">Fecha:</span> {detalleModal.fecha}</div>
              <div><span className="text-gray-500">Estado:</span> {estadoBadge(detalleModal.estado_diferencia)}</div>
              <div><span className="text-gray-500">Lotería:</span> {formatCurrency(detalleModal.total_ventas_loteria)}</div>
              <div><span className="text-gray-500">Premios pagados:</span> {formatCurrency(detalleModal.total_premios_pagados)}</div>
              <div><span className="text-gray-500">Recargas:</span> {formatCurrency(detalleModal.total_recargas)}</div>
              <div><span className="text-gray-500">Vales:</span> {formatCurrency(detalleModal.total_vales)}</div>
              <div><span className="text-gray-500">Ingresos extra:</span> {formatCurrency(detalleModal.total_ingresos_extra)}</div>
              <div><span className="text-gray-500">Lotería física:</span> {formatCurrency(detalleModal.total_loteria_fisica)}</div>
              <div><span className="text-gray-500">Total ingresos:</span> <strong>{formatCurrency(detalleModal.total_ingresos)}</strong></div>
              <div><span className="text-gray-500">Total egresos:</span> {formatCurrency(detalleModal.total_egresos)}</div>
              <div><span className="text-gray-500">Saldo teórico:</span> <strong>{formatCurrency(detalleModal.saldo_teorico)}</strong></div>
              <div><span className="text-gray-500">Pagos digitales:</span> {formatCurrency(detalleModal.total_pagos_digitales)}</div>
              <div><span className="text-gray-500">Efectivo contado:</span> <strong>{formatCurrency(detalleModal.efectivo_contado)}</strong></div>
              <div className="col-span-2">
                <span className="text-gray-500">Diferencia:</span>{' '}
                <strong className={`${
                  parseFloat(detalleModal.diferencia) > 0 ? 'text-yellow-700' : parseFloat(detalleModal.diferencia) < 0 ? 'text-red-700' : 'text-green-700'
                }`}>{formatCurrency(detalleModal.diferencia)}</strong>
              </div>
            </div>

            {detalleModal.notas && (
              <div className="bg-gray-50 rounded p-2">
                <span className="text-gray-500">Notas:</span> {detalleModal.notas}
              </div>
            )}

            {detalleModal.anulado && (
              <div className="bg-red-50 border border-red-200 rounded p-2">
                <p className="text-red-700 font-medium">Cierre anulado</p>
                <p className="text-red-600 text-xs">Motivo: {detalleModal.motivo_anulacion}</p>
                <p className="text-red-600 text-xs">Anulado: {detalleModal.anulado_at}</p>
              </div>
            )}

            {detalleModal.vales?.length > 0 && (
              <div>
                <h3 className="font-medium text-gray-800 mb-2">Vales</h3>
                <table className="w-full text-xs">
                  <thead><tr className="bg-gray-50 text-gray-600"><th className="px-2 py-1 text-left">Justificación</th><th className="px-2 py-1 text-right">Monto</th></tr></thead>
                  <tbody>{detalleModal.vales.map((v, i) => (
                    <tr key={i} className="border-t"><td className="px-2 py-1">{v.justificacion}</td><td className="px-2 py-1 text-right">{formatCurrency(v.monto)}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            )}

            {detalleModal.loteria_fisica?.length > 0 && (
              <div>
                <h3 className="font-medium text-gray-800 mb-2">Lotería Física</h3>
                <table className="w-full text-xs">
                  <thead><tr className="bg-gray-50 text-gray-600"><th className="px-2 py-1 text-left">Nombre</th><th className="px-2 py-1 text-center">Inic.</th><th className="px-2 py-1 text-center">Final</th><th className="px-2 py-1 text-right">Total</th></tr></thead>
                  <tbody>{detalleModal.loteria_fisica.map((lf, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-2 py-1">{lf.nombre}</td>
                      <td className="px-2 py-1 text-center">{lf.cantidad_inicial}</td>
                      <td className="px-2 py-1 text-center">{lf.cantidad_final}</td>
                      <td className="px-2 py-1 text-right">{formatCurrency(lf.total_vendido)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            </div>

            <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
              <Button
                onClick={() => imprimirCierre(detalleModal.id)}
                className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Imprimir
              </Button>
            </div>
          </>
        )}
      </Modal>

      {/* Modal de anulación */}
      <Modal isOpen={showAnularModal} onClose={() => setShowAnularModal(false)} title="Anular cierre de caja">
        <div className="space-y-3">
          <p className="text-sm text-gray-600">¿Seguro que desea anular este cierre? Ingrese el motivo:</p>
          <textarea
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            rows={3}
            value={motivoAnulacion}
            onChange={e => setMotivoAnulacion(e.target.value)}
            placeholder="Motivo de la anulación (mínimo 5 caracteres)…"
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => { setShowAnularModal(false); setMotivoAnulacion(''); }}>Cancelar</Button>
            <Button
              onClick={handleAnular}
              disabled={motivoAnulacion.length < 5 || anulando}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {anulando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Anular cierre
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
